import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Sparkles, Save, Trash2, ChevronDown, ChevronUp,
  Clock, Award, BookOpen, ArrowLeft, Calendar, Loader2, Shield,
  Database, Shuffle, AlertTriangle, CheckCircle, RefreshCw
} from 'lucide-react';
import { authenticatedFetch, getUser } from '../lib/auth';
import { useToast, useConfirm } from './ui';
import AssessmentFrameworkIndicator, { type CompatResult } from './AssessmentFrameworkIndicator';
import RubricPicker from './RubricPicker';
import {
  generateExamQuestions,
  createExamSet,
  updateExamSet,
  getExamSet,
  previewQuestionBank,
  validateQuestionBank,
  generateQuestionsToBank,
  getQuestionBankQuestions,
  reviewQuestion,
  getLiteratureStatus,
  archiveStudyPlanLiterature,
  type LiteratureStatus
} from '../lib/examApi';
import type { StudyPlan } from '../types';
import type { ExamQuestion, GradingScale, AIGeneratedExam, DifficultyDistribution, QuestionBankPreview, QuestionBankValidation } from '../types/exam';

const ExamCreator: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  const toast = useToast();
  const { confirm } = useConfirm();

  // State
  const [step, setStep] = useState<'select' | 'generate' | 'edit'>('select');
  const [studyPlans, setStudyPlans] = useState<StudyPlan[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');

  // Task #192: Modul-subset
  interface PlanModule {
    id: string;
    module_code: string;
    title: string;
    ects_credits: number | null;
    estimated_hours: number | null;
  }
  const [planModules, setPlanModules] = useState<PlanModule[]>([]);
  const [includedModuleIds, setIncludedModuleIds] = useState<string[]>([]);
  const [moduleCoverage, setModuleCoverage] = useState<'full' | 'subset'>('full');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timeLimitMinutes, setTimeLimitMinutes] = useState<number | undefined>(60);
  const [passThreshold, setPassThreshold] = useState(60);
  const [gradingScale, setGradingScale] = useState<GradingScale>('pass_fail');
  const [validityYears, setValidityYears] = useState<number | undefined>(undefined);
  const [sebRequired, setSebRequired] = useState(true);
  // Fase 0 (Task #48): planlagt primær vurderingsform
  const [primaryAssessmentType, setPrimaryAssessmentType] = useState<'written' | 'oral' | 'observation' | 'practical' | 'other'>('written');

  // Fase 2.6 Blokk E: kompatibilitet mot studieplanens vurderingsramme
  const [frameworkCompat, setFrameworkCompat] = useState<CompatResult | null>(null);
  const [frameworkRationale, setFrameworkRationale] = useState('');
  // Fase 2.6 Blokk G: rubric-forankring på prøvesett
  const [rubricId, setRubricId] = useState<string | null>(null);
  const [questions, setQuestions] = useState<ExamQuestion[]>([]);

  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set());

  // Question bank mode state
  const [useQuestionBank, setUseQuestionBank] = useState(false);
  const [questionCount, setQuestionCount] = useState(20);
  const [difficultyDistribution, setDifficultyDistribution] = useState<DifficultyDistribution>({
    easy: 0.2,
    medium: 0.6,
    hard: 0.2
  });
  const [bankPreview, setBankPreview] = useState<QuestionBankPreview | null>(null);
  const [bankValidation, setBankValidation] = useState<QuestionBankValidation | null>(null);
  const [loadingBankPreview, setLoadingBankPreview] = useState(false);
  const [generatingToBank, setGeneratingToBank] = useState(false);

  // Bank questions state
  interface BankQuestion {
    id: string;
    question_text: string;
    question_type: string;
    difficulty: string;
    status: string;
    times_used: number;
    module_title?: string;
    literature_title?: string;
    answer_options?: Array<{ id: string; text: string; is_correct: boolean }>;
    explanation?: string;
  }
  const [bankQuestions, setBankQuestions] = useState<BankQuestion[]>([]);
  const [loadingBankQuestions, setLoadingBankQuestions] = useState(false);
  const [bankFilter, setBankFilter] = useState<'all' | 'draft' | 'approved' | 'rejected'>('all');
  const [reviewingQuestionId, setReviewingQuestionId] = useState<string | null>(null);
  const [showRejectedQuestions, setShowRejectedQuestions] = useState(false);

  // Manual question entry state
  const [showManualForm, setShowManualForm] = useState(false);
  const [manualQuestion, setManualQuestion] = useState({
    question_text: '',
    question_type: 'multiple_choice' as 'multiple_choice' | 'true_false' | 'written',
    difficulty: 'medium' as 'easy' | 'medium' | 'hard',
    options: [
      { text: '', is_correct: true },
      { text: '', is_correct: false },
      { text: '', is_correct: false },
      { text: '', is_correct: false }
    ],
    grading_criteria: ''
  });
  const [savingManualQuestion, setSavingManualQuestion] = useState(false);

  // Edit question modal state
  const [editingQuestion, setEditingQuestion] = useState<BankQuestion | null>(null);
  const [editFormData, setEditFormData] = useState<{
    question_text: string;
    difficulty: 'easy' | 'medium' | 'hard';
    answer_options: Array<{ id?: string; text: string; is_correct: boolean }>;
    explanation: string;
  } | null>(null);
  const [savingEditQuestion, setSavingEditQuestion] = useState(false);

  // Literature archive state
  const [literatureStatus, setLiteratureStatus] = useState<LiteratureStatus[]>([]);
  const [archivingLiterature, setArchivingLiterature] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  useEffect(() => {
    loadStudyPlans();
    if (isEditing) {
      loadExistingExam();
    }
  }, [id]);

  // Check and download literature when question bank is enabled
  useEffect(() => {
    if (useQuestionBank && selectedPlanId) {
      checkLiteratureStatus();
    }
  }, [useQuestionBank, selectedPlanId]);

  // Task #192: Last moduler når studieplan velges
  useEffect(() => {
    if (!selectedPlanId) {
      setPlanModules([]);
      setIncludedModuleIds([]);
      setModuleCoverage('full');
      return;
    }
    (async () => {
      try {
        const res = await authenticatedFetch(`/api/ai/modules/${selectedPlanId}`);
        const data = await res.json();
        if (data.success && Array.isArray(data.modules)) {
          setPlanModules(data.modules);
        } else {
          setPlanModules([]);
        }
      } catch (err) {
        console.error('Failed to load study plan modules:', err);
        setPlanModules([]);
      }
    })();
  }, [selectedPlanId]);

  const checkLiteratureStatus = async () => {
    if (!selectedPlanId) return;
    try {
      const status = await getLiteratureStatus(selectedPlanId);
      setLiteratureStatus(status);

      // Check if any literature needs downloading
      const pendingCount = status.filter(lit => lit.download_status === 'pending').length;
      if (pendingCount > 0 && !archivingLiterature) {
        // Auto-trigger download
        handleArchiveLiterature();
      }
    } catch (err) {
      console.error('Failed to check literature status:', err);
    }
  };

  const handleArchiveLiterature = async () => {
    if (!selectedPlanId || archivingLiterature) return;

    setArchivingLiterature(true);
    setArchiveError(null);

    try {
      const result = await archiveStudyPlanLiterature(selectedPlanId);

      // Refresh status
      const status = await getLiteratureStatus(selectedPlanId);
      setLiteratureStatus(status);

      if (result.failed > 0) {
        setArchiveError(`${result.failed} av ${result.archived + result.failed} kilder kunne ikke lastes ned. Spørsmål vil bare genereres fra tilgjengelige kilder.`);
      }
    } catch (err: any) {
      setArchiveError('Kunne ikke laste ned litteratur: ' + err.message);
    } finally {
      setArchivingLiterature(false);
    }
  };

  // Load question bank preview when study plan is selected and question bank mode is enabled
  const loadQuestionBankPreview = useCallback(async () => {
    if (!selectedPlanId || !useQuestionBank) return;

    setLoadingBankPreview(true);
    try {
      const [preview, validation] = await Promise.all([
        previewQuestionBank(selectedPlanId, questionCount, difficultyDistribution),
        validateQuestionBank(selectedPlanId, questionCount)
      ]);
      // Ensure preview has expected structure
      setBankPreview(preview && preview.available ? preview : {
        available: { total: 0, byDifficulty: { easy: 0, medium: 0, hard: 0 } },
        requested: { total: questionCount, byDifficulty: difficultyDistribution },
        canFulfill: { easy: 0, medium: 0, hard: 0 },
        shortfall: { easy: 0, medium: 0, hard: 0 }
      });
      // Ensure validation has expected structure
      setBankValidation(validation ? {
        valid: validation.valid ?? false,
        issues: validation.issues || [],
        availability: validation.availability || { total: 0, byDifficulty: { easy: 0, medium: 0, hard: 0 } },
        recommendations: validation.recommendations || []
      } : null);
    } catch (err: any) {
      console.error('Failed to load question bank preview:', err);
      // Set safe defaults on error
      setBankPreview({
        available: { total: 0, byDifficulty: { easy: 0, medium: 0, hard: 0 } },
        requested: { total: questionCount, byDifficulty: difficultyDistribution },
        canFulfill: { easy: 0, medium: 0, hard: 0 },
        shortfall: { easy: 0, medium: 0, hard: 0 }
      });
      setBankValidation({
        valid: false,
        issues: [{ type: 'error', message: 'Kunne ikke laste spørsmålsbank-status', severity: 'error' }],
        availability: { total: 0, byDifficulty: { easy: 0, medium: 0, hard: 0 } },
        recommendations: []
      });
    } finally {
      setLoadingBankPreview(false);
    }
  }, [selectedPlanId, useQuestionBank, questionCount, difficultyDistribution]);

  useEffect(() => {
    loadQuestionBankPreview();
  }, [loadQuestionBankPreview]);

  // Load bank questions when in question bank mode
  const loadBankQuestions = useCallback(async () => {
    if (!selectedPlanId || !useQuestionBank) {
      setBankQuestions([]);
      return;
    }

    setLoadingBankQuestions(true);
    try {
      const statusFilter = bankFilter === 'all' ? undefined : bankFilter;
      const result = await getQuestionBankQuestions(selectedPlanId, { status: statusFilter, limit: 200 });
      setBankQuestions(Array.isArray(result?.questions) ? result.questions : []);
    } catch (err: any) {
      console.error('Failed to load bank questions:', err);
      setBankQuestions([]);
    } finally {
      setLoadingBankQuestions(false);
    }
  }, [selectedPlanId, useQuestionBank, bankFilter]);

  useEffect(() => {
    loadBankQuestions();
  }, [loadBankQuestions]);

  const handleReviewQuestion = async (questionId: string, status: 'approved' | 'rejected' | 'draft') => {
    setReviewingQuestionId(questionId);
    try {
      await reviewQuestion(questionId, status);
      // Reload questions and preview
      await Promise.all([loadBankQuestions(), loadQuestionBankPreview()]);
    } catch (err: any) {
      setError('Kunne ikke oppdatere spørsmål: ' + err.message);
    } finally {
      setReviewingQuestionId(null);
    }
  };

  const openEditQuestion = (question: BankQuestion) => {
    setEditingQuestion(question);
    setEditFormData({
      question_text: question.question_text,
      difficulty: question.difficulty as 'easy' | 'medium' | 'hard',
      answer_options: question.answer_options?.map(opt => ({
        id: opt.id,
        text: opt.text,
        is_correct: opt.is_correct
      })) || [],
      explanation: question.explanation || ''
    });
  };

  const handleSaveEditQuestion = async () => {
    if (!editingQuestion || !editFormData) return;

    setSavingEditQuestion(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/questions/${editingQuestion.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          question_text: editFormData.question_text,
          difficulty: editFormData.difficulty,
          answer_options: editFormData.answer_options,
          explanation: editFormData.explanation
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      // Close modal and reload questions
      setEditingQuestion(null);
      setEditFormData(null);
      await loadBankQuestions();
    } catch (err: any) {
      setError('Kunne ikke oppdatere spørsmål: ' + err.message);
    } finally {
      setSavingEditQuestion(false);
    }
  };

  const handleSaveManualQuestion = async () => {
    if (!selectedPlanId) {
      setError('Ingen studieplan valgt');
      return;
    }
    if (!manualQuestion.question_text.trim()) {
      setError('Spørsmålstekst er påkrevd');
      return;
    }

    setSavingManualQuestion(true);
    setError(null);

    try {
      const response = await authenticatedFetch(`/api/questions/manual`, {
        method: 'POST',
        body: JSON.stringify({
          study_plan_id: selectedPlanId,
          question_text: manualQuestion.question_text,
          question_type: manualQuestion.question_type,
          difficulty: manualQuestion.difficulty,
          options: manualQuestion.question_type !== 'written' ? manualQuestion.options : undefined,
          grading_criteria: manualQuestion.question_type === 'written' ? manualQuestion.grading_criteria : undefined,
          status: 'draft'
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      // Reset form and reload
      setManualQuestion({
        question_text: '',
        question_type: 'multiple_choice',
        difficulty: 'medium',
        options: [
          { text: '', is_correct: true },
          { text: '', is_correct: false },
          { text: '', is_correct: false },
          { text: '', is_correct: false }
        ],
        grading_criteria: ''
      });
      setShowManualForm(false);
      await Promise.all([loadBankQuestions(), loadQuestionBankPreview()]);
    } catch (err: any) {
      setError('Kunne ikke lagre spørsmål: ' + err.message);
    } finally {
      setSavingManualQuestion(false);
    }
  };

  const loadStudyPlans = async () => {
    try {
      const user = await getUser();
      if (!user) throw new Error('Not authenticated');

      const response = await authenticatedFetch('/api/study-plans/list', {
        method: 'POST',
        body: JSON.stringify({ user_id: user.id })
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.error);
      setStudyPlans(data.study_plans.filter((sp: StudyPlan) => sp.status === 'approved'));
    } catch (err: any) {
      setError('Kunne ikke laste studieplaner: ' + err.message);
    }
  };

  const loadExistingExam = async () => {
    if (!id) return;
    try {
      const { exam_set, questions: existingQuestions } = await getExamSet(id);

      console.log('[ExamCreator] Loaded exam_set:', exam_set);
      console.log('[ExamCreator] use_question_bank:', exam_set.use_question_bank, typeof exam_set.use_question_bank);

      setSelectedPlanId(exam_set.study_plan_id);
      setTitle(exam_set.title);
      setDescription(exam_set.description || '');
      setTimeLimitMinutes(exam_set.time_limit_minutes);
      setPassThreshold(exam_set.pass_threshold);
      setGradingScale(exam_set.grading_scale);
      setValidityYears(exam_set.validity_years);
      setSebRequired(exam_set.seb_required !== false);
      if (exam_set.primary_assessment_type) {
        setPrimaryAssessmentType(exam_set.primary_assessment_type);
      }
      // Fase 2.6 Blokk G: load eksisterende rubric-tilknytning + rationale
      if ((exam_set as any).rubric_id) {
        setRubricId((exam_set as any).rubric_id);
      }
      if ((exam_set as any).framework_rationale) {
        setFrameworkRationale((exam_set as any).framework_rationale);
      }
      setQuestions(existingQuestions);

      // Restore question bank settings
      const useBank = exam_set.use_question_bank === true;
      if (useBank) {
        setUseQuestionBank(true);
        if (exam_set.question_count) setQuestionCount(exam_set.question_count);
        if (exam_set.difficulty_distribution) setDifficultyDistribution(exam_set.difficulty_distribution);
      }

      setStep('edit');
    } catch (err: any) {
      setError('Kunne ikke laste prøvesett: ' + err.message);
    }
  };

  const handleGenerateQuestions = async () => {
    if (!selectedPlanId) {
      toast.showWarning('Velg en studieplan først');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const generated: AIGeneratedExam = await generateExamQuestions(selectedPlanId);

      setTitle(generated.suggested_title);
      setDescription(generated.suggested_description);
      setTimeLimitMinutes(generated.suggested_time_limit_minutes);
      setPassThreshold(generated.suggested_pass_threshold);
      setGradingScale(generated.suggested_grading_scale);
      setValidityYears(generated.suggested_validity_years || undefined);
      setQuestions(generated.questions);

      setStep('edit');
      setExpandedQuestions(new Set(generated.questions.map((_, i) => i)));
    } catch (err: any) {
      setError('KI-generering feilet: ' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateToBank = async () => {
    if (!selectedPlanId) {
      setError('Ingen studieplan valgt. Velg en studieplan først.');
      return;
    }

    setGeneratingToBank(true);
    setError(null);

    try {
      console.log('[QuestionBank] Generating questions for study plan:', selectedPlanId);
      console.log('[QuestionBank] Minimum questions needed:', questionCount);

      const result = await generateQuestionsToBank(selectedPlanId, {
        questionsPerLiterature: 5,
        difficultyDistribution,
        minimumQuestions: questionCount  // Pass required question count
      });

      console.log('[QuestionBank] Generation result:', result);
      const newCount = result.totalQuestionsGenerated || result.generated || 0;
      const existingBefore = result.existingQuestionsBefore || 0;

      let message = `${newCount} nye spørsmål generert til spørsmålsbanken.`;
      if (existingBefore > 0) {
        message += ` ${existingBefore} spørsmål fantes fra før (ikke duplisert).`;
      }

      // Show warning if there's a duplicate limitation
      if (result.duplicateWarning) {
        message += ` ⚠️ ${result.duplicateWarning}`;
      }

      message += ' Gjennomgå og godkjenn dem nedenfor.';
      toast.showSuccess(message);

      // Reload questions and preview after generating
      await Promise.all([loadBankQuestions(), loadQuestionBankPreview()]);
    } catch (err: any) {
      console.error('[QuestionBank] Generation error:', err);
      setError('Generering til spørsmålsbank feilet: ' + err.message);
    } finally {
      setGeneratingToBank(false);
    }
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.showWarning('Tittel er påkrevd');
      return;
    }

    // In question bank mode, we don't need fixed questions
    if (!useQuestionBank && questions.length === 0) {
      toast.showWarning('Legg til minst ett spørsmål');
      return;
    }

    // In question bank mode, validate there are enough approved questions
    if (useQuestionBank && bankValidation && !bankValidation.valid) {
      const proceed = await confirm({
        title: 'Ikke nok spørsmål',
        message: 'Spørsmålsbanken har ikke nok godkjente spørsmål for ønsket konfigurasjon. Vil du fortsette likevel? Prøven vil ikke fungere før det er nok spørsmål.',
        confirmLabel: 'Fortsett likevel',
        cancelLabel: 'Avbryt',
        variant: 'warning'
      });
      if (!proceed) return;
    }

    // Fase 2.6 Blokk E: pre-blokk hvis vurderingsrammen avviser
    if (frameworkCompat?.blocking) {
      toast.showError('Prøvesettet matcher ikke studieplanens vurderingsramme (streng modus). Endre valgene eller be studieplan-eier endre fleksibiliteten.');
      return;
    }
    if (frameworkCompat?.requires_rationale && frameworkRationale.trim().length < 50) {
      toast.showWarning('Studieplanens vurderingsramme krever begrunnelse for avvik (minst 50 tegn).');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const data: any = {
        study_plan_id: selectedPlanId,
        title: title.trim(),
        description: description.trim() || undefined,
        time_limit_minutes: timeLimitMinutes,
        pass_threshold: passThreshold,
        grading_scale: gradingScale,
        validity_years: validityYears,
        seb_required: sebRequired,
        questions: useQuestionBank ? [] : questions,
        use_question_bank: useQuestionBank,
        question_count: useQuestionBank ? questionCount : undefined,
        difficulty_distribution: useQuestionBank ? difficultyDistribution : undefined,
        // Task #192: send valgte moduler (kun ved subset; ved 'full' lagres NULL = hele studieplan)
        included_module_ids: moduleCoverage === 'subset' && includedModuleIds.length > 0
          ? includedModuleIds
          : null,
        // Fase 0 (Task #48): planlagt primær vurderingsform
        primary_assessment_type: primaryAssessmentType,
        // Fase 2.6 Blokk E: send begrunnelse hvis vurderingsrammen krever det
        framework_rationale: frameworkCompat?.requires_rationale ? frameworkRationale.trim() : undefined,
        // Fase 2.6 Blokk G: rubric-forankring
        has_rubric: Boolean(rubricId),
        rubric_id: rubricId,
      };

      if (isEditing && id) {
        await updateExamSet(id, data);
        toast.showSuccess('Prøvesett oppdatert!');
      } else {
        await createExamSet(data);
        toast.showSuccess('Prøvesett opprettet!');
      }

      navigate('/exams');
    } catch (err: any) {
      setError('Kunne ikke lagre: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = (type: ExamQuestion['question_type']) => {
    const newQuestion: ExamQuestion = {
      question_type: type,
      question_text: '',
      points: 1,
      options: type === 'multiple_choice' ? [
        { text: '', is_correct: true },
        { text: '', is_correct: false },
        { text: '', is_correct: false },
        { text: '', is_correct: false }
      ] : type === 'true_false' ? [
        { text: 'Sant', is_correct: true },
        { text: 'Usant', is_correct: false }
      ] : undefined,
      grading_criteria: type === 'written' ? '' : undefined
    };

    setQuestions([...questions, newQuestion]);
    setExpandedQuestions(new Set([...expandedQuestions, questions.length]));
  };

  const updateQuestion = (index: number, updates: Partial<ExamQuestion>) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], ...updates };
    setQuestions(updated);
  };

  const deleteQuestion = async (index: number) => {
    const confirmed = await confirm({
      title: 'Slett spørsmål',
      message: 'Slett dette spørsmålet?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });
    if (!confirmed) return;
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const toggleQuestionExpanded = (index: number) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  const updateOption = (questionIndex: number, optionIndex: number, text: string) => {
    const updated = [...questions];
    if (updated[questionIndex].options) {
      updated[questionIndex].options![optionIndex].text = text;
      setQuestions(updated);
    }
  };

  const setCorrectOption = (questionIndex: number, optionIndex: number) => {
    const updated = [...questions];
    if (updated[questionIndex].options) {
      updated[questionIndex].options = updated[questionIndex].options!.map((opt, i) => ({
        ...opt,
        is_correct: i === optionIndex
      }));
      setQuestions(updated);
    }
  };

  const getQuestionTypeLabel = (type: ExamQuestion['question_type']) => {
    const labels = {
      multiple_choice: 'Flervalg',
      written: 'Skriftlig',
      true_false: 'Sant/Usant'
    };
    return labels[type];
  };


  // Render Step 1: Select Study Plan
  if (step === 'select' && !isEditing) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/exams')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft size={20} />
          Tilbake til prøver
        </button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Opprett nytt prøvesett</h1>
        <p className="text-gray-600 mb-8">Velg studieplan du vil lage prøve for</p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Velg studieplan
          </label>
          <select
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          >
            <option value="">-- Velg studieplan --</option>
            {studyPlans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.title} ({plan.ects_credits} KP)
              </option>
            ))}
          </select>

          {/* Task #192: Modul-utvalg (vises når studieplan har moduler) */}
          {selectedPlanId && planModules.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hva dekker dette prøvesettet?
              </label>
              <div className="flex gap-4 mb-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="moduleCoverage"
                    checked={moduleCoverage === 'full'}
                    onChange={() => { setModuleCoverage('full'); setIncludedModuleIds([]); }}
                  />
                  <span>Hele studieplanen ({planModules.length} moduler)</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="moduleCoverage"
                    checked={moduleCoverage === 'subset'}
                    onChange={() => setModuleCoverage('subset')}
                  />
                  <span>Et utvalg moduler</span>
                </label>
              </div>

              {moduleCoverage === 'subset' && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 space-y-2">
                  <p className="text-xs text-gray-600 mb-2">
                    Velg modulene som dette prøvesettet skal dekke.
                    <strong> Kun spørsmål tagget til disse modulene blir tilgjengelige</strong>,
                    og kompetanseattesten vil reflektere kun disse modulenes læringsutbytter.
                  </p>
                  {planModules.map((mod) => {
                    const checked = includedModuleIds.includes(mod.id);
                    return (
                      <label key={mod.id} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white p-2 rounded">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setIncludedModuleIds([...includedModuleIds, mod.id]);
                            } else {
                              setIncludedModuleIds(includedModuleIds.filter(x => x !== mod.id));
                            }
                          }}
                          className="mt-0.5"
                        />
                        <div className="flex-1">
                          <div className="font-medium">{mod.module_code}: {mod.title}</div>
                          <div className="text-xs text-gray-500">
                            {mod.ects_credits ? `${mod.ects_credits} Kompetansepoeng · ` : ''}
                            {mod.estimated_hours ? `${mod.estimated_hours}t` : ''}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                  {includedModuleIds.length > 0 && (
                    <div className="text-xs text-kf-blue-700 font-medium pt-2 border-t border-gray-200">
                      Aggregert Kompetansepoeng for prøvesettet: {
                        planModules
                          .filter(m => includedModuleIds.includes(m.id))
                          .reduce((sum, m) => sum + (m.ects_credits || 0), 0)
                      }
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex gap-4">
            <button
              onClick={handleGenerateQuestions}
              disabled={!selectedPlanId || generating}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-kf-orange-600 to-kf-orange-500 text-white rounded-lg hover:from-kf-orange-700 hover:to-kf-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {generating ? (
                <Loader2 size={20} className="animate-spin" />
              ) : (
                <Sparkles size={20} />
              )}
              {generating ? 'Genererer med KI...' : 'Generer spørsmål med KI'}
            </button>

            <button
              onClick={() => {
                if (!selectedPlanId) {
                  toast.showWarning('Velg en studieplan først');
                  return;
                }
                setStep('edit');
              }}
              disabled={!selectedPlanId}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Start fra bunnen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render Step 2: Edit/Create Exam
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <button
        onClick={() => step === 'edit' && !isEditing ? setStep('select') : navigate('/exams')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        {step === 'edit' && !isEditing ? 'Tilbake til valg' : 'Tilbake til prøver'}
      </button>

      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Rediger prøvesett' : 'Opprett prøvesett'}
          </h1>
          <p className="text-gray-600 mt-1">Konfigurer prøven og legg til spørsmål</p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 disabled:opacity-50 transition"
        >
          <Save size={20} />
          {saving ? 'Lagrer...' : 'Lagre prøvesett'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Settings Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Prøveinnstillinger</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tittel *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
              placeholder="Eksempel: Avsluttende prøve for ledelseskurs"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Beskrivelse
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
              placeholder="Kort beskrivelse av prøven..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock size={16} className="inline mr-1" />
              Tidsbegrensning (minutter)
            </label>
            <input
              type="number"
              value={timeLimitMinutes || ''}
              onChange={(e) => setTimeLimitMinutes(e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
              placeholder="Ingen grense"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Award size={16} className="inline mr-1" />
              Beståttgrense (%)
            </label>
            <input
              type="number"
              value={passThreshold}
              onChange={(e) => setPassThreshold(parseInt(e.target.value) || 60)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
              min="0"
              max="100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <BookOpen size={16} className="inline mr-1" />
              Karakterskala
            </label>
            <select
              value={gradingScale}
              onChange={(e) => setGradingScale(e.target.value as GradingScale)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
            >
              <option value="pass_fail">Bestått/Ikke bestått</option>
              <option value="three_tier">Meget godt bestått/Bestått/Ikke bestått</option>
              <option value="a_f">A-F</option>
              <option value="percentage">Prosent</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <BookOpen size={16} className="inline mr-1" />
              Planlagt vurderingsform
            </label>
            <select
              value={primaryAssessmentType}
              onChange={(e) => setPrimaryAssessmentType(e.target.value as typeof primaryAssessmentType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
            >
              <option value="written">Skriftlig prøve (SEB, standard)</option>
              <option value="oral">Muntlig eksamen</option>
              <option value="practical">Praktisk demonstrasjon</option>
              <option value="observation">Kontinuerlig observasjon</option>
              <option value="other">Annen vurderingsform</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Skriftlig SEB-prøve er standard. For andre vurderingsformer sensurerer sensor manuelt uten spørsmålsbank. Sensor kan alltid overstyre til alternativ vurdering ved teknisk svikt uansett planlagt form.
            </p>
            {primaryAssessmentType !== 'written' && (
              <div className="mt-2 rounded-lg border border-purple-200 bg-purple-50 p-3 text-sm text-purple-900">
                <span className="font-semibold">Merk:</span> Med planlagt {
                  primaryAssessmentType === 'oral' ? 'muntlig eksamen'
                  : primaryAssessmentType === 'practical' ? 'praktisk demonstrasjon'
                  : primaryAssessmentType === 'observation' ? 'kontinuerlig observasjon'
                  : 'annen vurderingsform'
                } trenger ikke prøvesettet spørsmål — sensor registrerer sensur direkte etter gjennomført vurdering. SEB deaktiveres automatisk. Kompositt-vurdering (skriftlig + prosjekt osv.) kommer i senere fase.
              </div>
            )}

            {/* Fase 2.6 Blokk G: rubric-forankring (vises for alle planer; rammen avgjør om påkrevd) */}
            {selectedPlanId && (
              <div className="mt-4">
                <RubricPicker
                  value={rubricId}
                  onChange={setRubricId}
                  required={Boolean(
                    frameworkCompat?.enforced &&
                    frameworkCompat?.mismatches?.some((m: string) => /rubric/i.test(m))
                  )}
                  filterByComponentType={
                    primaryAssessmentType === 'practical' ? 'practical' :
                    primaryAssessmentType === 'oral' ? 'oral' :
                    primaryAssessmentType === 'observation' ? 'observation' :
                    primaryAssessmentType === 'written' ? 'written' : undefined
                  }
                />
              </div>
            )}

            {/* Fase 2.6 Blokk E: live kompatibilitets-indikator mot studieplanens vurderingsramme.
                 Blokk G: has_rubric utledes nå fra rubricId så indikatoren reflekterer ekte tilknytning. */}
            {selectedPlanId && (
              <div className="mt-3">
                <AssessmentFrameworkIndicator
                  studyPlanId={selectedPlanId}
                  implementationType="exam_set"
                  implementationData={{
                    primary_assessment_type: primaryAssessmentType,
                    has_rubric: Boolean(rubricId),
                    assessor_type: 'any',
                  }}
                  rationale={frameworkRationale}
                  onRationaleChange={setFrameworkRationale}
                  onResultChange={setFrameworkCompat}
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar size={16} className="inline mr-1" />
              Gyldighet (år)
            </label>
            <input
              type="number"
              value={validityYears || ''}
              onChange={(e) => setValidityYears(e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
              placeholder="Evig gyldig"
              min="1"
            />
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <Shield size={20} className="text-kf-blue-600 mt-0.5" />
                <div>
                  <label htmlFor="seb-toggle" className="block text-sm font-medium text-gray-900">
                    Krev Safe Exam Browser
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Når aktivert må kandidater bruke Safe Exam Browser for å gjennomføre prøven.
                    Dette forhindrer juks ved å låse datamaskinen under prøvegjennomføringen.
                  </p>
                </div>
              </div>
              <button
                id="seb-toggle"
                type="button"
                role="switch"
                aria-checked={sebRequired}
                onClick={() => setSebRequired(!sebRequired)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-kf-blue-500 focus:ring-offset-2 ${
                  sebRequired ? 'bg-kf-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    sebRequired ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Question Bank Mode Toggle */}
          <div className="md:col-span-2">
            <div className={`flex items-center justify-between p-4 rounded-lg border ${
              useQuestionBank ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-start gap-3">
                <Database size={20} className={useQuestionBank ? 'text-purple-600' : 'text-gray-500'} />
                <div>
                  <label htmlFor="question-bank-toggle" className="block text-sm font-medium text-gray-900">
                    Bruk spørsmålsbank (stratifisert uttrekk)
                  </label>
                  <p className="text-xs text-gray-600 mt-1">
                    Hver kandidat får et tilfeldig utvalg av spørsmål fra en godkjent bank.
                    Spørsmålene fordeles etter vanskelighetsgrad (enkel/middels/vanskelig).
                  </p>
                </div>
              </div>
              <button
                id="question-bank-toggle"
                type="button"
                role="switch"
                aria-checked={useQuestionBank}
                onClick={() => setUseQuestionBank(!useQuestionBank)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  useQuestionBank ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    useQuestionBank ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Question Bank Configuration */}
            {useQuestionBank && (
              <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                <div className="flex items-center gap-2 text-purple-800">
                  <Shuffle size={18} />
                  <span className="font-medium">Spørsmålsbank-konfigurasjon</span>
                </div>

                {/* Question Count */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Antall spørsmål per kandidat
                  </label>
                  <input
                    type="number"
                    value={questionCount}
                    onChange={(e) => setQuestionCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    min="1"
                    max="100"
                  />
                </div>

                {/* Difficulty Distribution */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vanskelighetsfordeling
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs text-green-700 mb-1">Enkel ({Math.round(difficultyDistribution.easy * 100)}%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={difficultyDistribution.easy * 100}
                        onChange={(e) => {
                          const easy = parseInt(e.target.value) / 100;
                          const remaining = 1 - easy;
                          const ratio = difficultyDistribution.medium + difficultyDistribution.hard;
                          setDifficultyDistribution({
                            easy,
                            medium: ratio > 0 ? remaining * (difficultyDistribution.medium / ratio) : remaining * 0.75,
                            hard: ratio > 0 ? remaining * (difficultyDistribution.hard / ratio) : remaining * 0.25
                          });
                        }}
                        className="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-yellow-700 mb-1">Middels ({Math.round(difficultyDistribution.medium * 100)}%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={difficultyDistribution.medium * 100}
                        onChange={(e) => {
                          const medium = parseInt(e.target.value) / 100;
                          const remaining = 1 - medium;
                          const ratio = difficultyDistribution.easy + difficultyDistribution.hard;
                          setDifficultyDistribution({
                            easy: ratio > 0 ? remaining * (difficultyDistribution.easy / ratio) : remaining * 0.5,
                            medium,
                            hard: ratio > 0 ? remaining * (difficultyDistribution.hard / ratio) : remaining * 0.5
                          });
                        }}
                        className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-red-700 mb-1">Vanskelig ({Math.round(difficultyDistribution.hard * 100)}%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={difficultyDistribution.hard * 100}
                        onChange={(e) => {
                          const hard = parseInt(e.target.value) / 100;
                          const remaining = 1 - hard;
                          const ratio = difficultyDistribution.easy + difficultyDistribution.medium;
                          setDifficultyDistribution({
                            easy: ratio > 0 ? remaining * (difficultyDistribution.easy / ratio) : remaining * 0.25,
                            medium: ratio > 0 ? remaining * (difficultyDistribution.medium / ratio) : remaining * 0.75,
                            hard
                          });
                        }}
                        className="w-full h-2 bg-red-200 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Forhåndsvisning: {Math.round(questionCount * difficultyDistribution.easy)} enkle,{' '}
                    {Math.round(questionCount * difficultyDistribution.medium)} middels,{' '}
                    {Math.round(questionCount * difficultyDistribution.hard)} vanskelige
                  </p>
                </div>

                {/* Literature Archive Status */}
                {literatureStatus.length > 0 && (
                  <div className="p-3 rounded-lg bg-kf-blue-50 border border-kf-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <BookOpen size={18} className="text-kf-blue-600" />
                        <span className="font-medium text-kf-blue-800">Litteraturkilder</span>
                      </div>
                      {archivingLiterature && (
                        <div className="flex items-center gap-1 text-kf-blue-600 text-sm">
                          <Loader2 size={14} className="animate-spin" />
                          <span>Laster ned...</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-1 text-sm">
                      {literatureStatus.map(lit => (
                        <div key={lit.id} className="flex items-center gap-2">
                          {lit.download_status === 'downloaded' ? (
                            <CheckCircle size={14} className="text-green-600" />
                          ) : lit.download_status === 'pending' ? (
                            <Loader2 size={14} className="text-kf-blue-600 animate-spin" />
                          ) : (
                            <AlertTriangle size={14} className="text-red-600" />
                          )}
                          <span className={`truncate ${
                            lit.download_status === 'failed' ? 'text-red-700' :
                            lit.download_status === 'downloaded' ? 'text-gray-700' :
                            'text-kf-blue-700'
                          }`}>
                            {lit.title}
                          </span>
                          {lit.download_status === 'failed' && lit.download_error && (
                            <span className="text-xs text-red-600">({lit.download_error})</span>
                          )}
                        </div>
                      ))}
                    </div>

                    {archiveError && (
                      <div className="mt-2 p-2 bg-amber-100 rounded text-sm text-amber-800">
                        <AlertTriangle size={14} className="inline mr-1" />
                        {archiveError}
                      </div>
                    )}

                    {literatureStatus.some(lit => lit.download_status === 'pending') && !archivingLiterature && (
                      <button
                        onClick={handleArchiveLiterature}
                        className="mt-2 text-sm text-kf-blue-700 hover:text-kf-blue-900 underline"
                      >
                        Last ned ventende kilder
                      </button>
                    )}
                  </div>
                )}

                {/* Question Bank Preview */}
                {loadingBankPreview ? (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 size={16} className="animate-spin" />
                    <span>Laster spørsmålsbank-status...</span>
                  </div>
                ) : bankValidation && (
                  <div className={`p-3 rounded-lg ${bankValidation.valid ? 'bg-green-100' : 'bg-amber-100'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {bankValidation.valid ? (
                        <CheckCircle size={18} className="text-green-600" />
                      ) : (
                        <AlertTriangle size={18} className="text-amber-600" />
                      )}
                      <span className={`font-medium ${bankValidation.valid ? 'text-green-800' : 'text-amber-800'}`}>
                        {bankValidation.valid
                          ? 'Spørsmålsbanken er klar'
                          : 'Spørsmålsbanken trenger flere spørsmål'}
                      </span>
                    </div>

                    {bankPreview?.available && (
                      <div className="text-sm text-gray-700 space-y-1">
                        <p>
                          Godkjente spørsmål: <strong>{bankPreview.available.total || 0}</strong> av{' '}
                          <strong>{questionCount}</strong> påkrevd
                        </p>
                        <p className="text-xs">
                          Enkel: {bankPreview.available.byDifficulty?.easy || 0} |
                          Middels: {bankPreview.available.byDifficulty?.medium || 0} |
                          Vanskelig: {bankPreview.available.byDifficulty?.hard || 0}
                        </p>
                      </div>
                    )}

                    {bankValidation.issues && bankValidation.issues.length > 0 && (
                      <ul className="mt-2 text-sm text-amber-700 list-disc list-inside">
                        {bankValidation.issues.map((issue, idx) => (
                          <li key={idx}>{issue.message}</li>
                        ))}
                      </ul>
                    )}

                    {!bankValidation.valid && (
                      <button
                        onClick={handleGenerateToBank}
                        disabled={generatingToBank}
                        className="mt-3 flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm transition"
                      >
                        {generatingToBank ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Sparkles size={16} />
                        )}
                        {generatingToBank ? 'Genererer...' : 'Generer flere spørsmål til banken'}
                      </button>
                    )}

                    <button
                      onClick={loadQuestionBankPreview}
                      className="mt-2 flex items-center gap-1 text-sm text-purple-700 hover:text-purple-900"
                    >
                      <RefreshCw size={14} />
                      Oppdater
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Questions Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {useQuestionBank ? 'Spørsmålsbank' : `Spørsmål (${questions.length})`}
        </h2>

        {/* Question Bank Mode */}
        {useQuestionBank ? (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-purple-900">Stratifisert spørsmålsbank</h3>
                  <p className="text-sm text-purple-700">
                    Hver kandidat får <strong>{questionCount}</strong> tilfeldige spørsmål
                  </p>
                </div>
                <div className="flex gap-2 text-sm">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                    {Math.round(questionCount * difficultyDistribution.easy)} enkle
                  </span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                    {Math.round(questionCount * difficultyDistribution.medium)} middels
                  </span>
                  <span className="px-2 py-1 bg-red-100 text-red-800 rounded">
                    {Math.round(questionCount * difficultyDistribution.hard)} vanskelige
                  </span>
                </div>
              </div>
            </div>

            {/* Error display for question bank operations */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                >
                  Lukk
                </button>
              </div>
            )}

            {/* Generate & Filter Controls */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Vis:</span>
                <select
                  value={bankFilter}
                  onChange={(e) => setBankFilter(e.target.value as typeof bankFilter)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                >
                  <option value="all">Alle spørsmål</option>
                  <option value="draft">Utkast (venter på godkjenning)</option>
                  <option value="approved">Godkjent</option>
                  <option value="rejected">Avvist</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowManualForm(!showManualForm)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition"
                >
                  + Legg til manuelt
                </button>
                <button
                  onClick={handleGenerateToBank}
                  disabled={generatingToBank}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium transition"
                >
                  {generatingToBank ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Sparkles size={16} />
                  )}
                  {generatingToBank ? 'Genererer fra litteratur...' : 'Generer spørsmål fra litteratur'}
                </button>
              </div>
            </div>

            {/* Manual Question Form */}
            {showManualForm && (
              <div className="border border-purple-200 rounded-lg p-4 bg-purple-50 space-y-4">
                <h4 className="font-medium text-purple-900">Legg til spørsmål manuelt</h4>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={manualQuestion.question_type}
                      onChange={(e) => setManualQuestion({
                        ...manualQuestion,
                        question_type: e.target.value as typeof manualQuestion.question_type,
                        options: e.target.value === 'true_false' ? [
                          { text: 'Sant', is_correct: true },
                          { text: 'Usant', is_correct: false }
                        ] : [
                          { text: '', is_correct: true },
                          { text: '', is_correct: false },
                          { text: '', is_correct: false },
                          { text: '', is_correct: false }
                        ]
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="multiple_choice">Flervalg</option>
                      <option value="true_false">Sant/Usant</option>
                      <option value="written">Skriftlig</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vanskelighetsgrad</label>
                    <select
                      value={manualQuestion.difficulty}
                      onChange={(e) => setManualQuestion({
                        ...manualQuestion,
                        difficulty: e.target.value as typeof manualQuestion.difficulty
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="easy">Enkel</option>
                      <option value="medium">Middels</option>
                      <option value="hard">Vanskelig</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spørsmålstekst *</label>
                  <textarea
                    value={manualQuestion.question_text}
                    onChange={(e) => setManualQuestion({ ...manualQuestion, question_text: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    placeholder="Skriv spørsmålet her..."
                  />
                </div>

                {manualQuestion.question_type === 'multiple_choice' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Svaralternativer (velg riktig)</label>
                    <div className="space-y-2">
                      {manualQuestion.options.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="correct-manual"
                            checked={opt.is_correct}
                            onChange={() => setManualQuestion({
                              ...manualQuestion,
                              options: manualQuestion.options.map((o, i) => ({ ...o, is_correct: i === idx }))
                            })}
                          />
                          <input
                            type="text"
                            value={opt.text}
                            onChange={(e) => setManualQuestion({
                              ...manualQuestion,
                              options: manualQuestion.options.map((o, i) => i === idx ? { ...o, text: e.target.value } : o)
                            })}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm"
                            placeholder={`Alternativ ${idx + 1}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {manualQuestion.question_type === 'true_false' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Riktig svar</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={manualQuestion.options[0]?.is_correct}
                          onChange={() => setManualQuestion({
                            ...manualQuestion,
                            options: [{ text: 'Sant', is_correct: true }, { text: 'Usant', is_correct: false }]
                          })}
                        />
                        Sant
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={manualQuestion.options[1]?.is_correct}
                          onChange={() => setManualQuestion({
                            ...manualQuestion,
                            options: [{ text: 'Sant', is_correct: false }, { text: 'Usant', is_correct: true }]
                          })}
                        />
                        Usant
                      </label>
                    </div>
                  </div>
                )}

                {manualQuestion.question_type === 'written' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vurderingskriterier</label>
                    <textarea
                      value={manualQuestion.grading_criteria}
                      onChange={(e) => setManualQuestion({ ...manualQuestion, grading_criteria: e.target.value })}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      placeholder="Beskriv hva som skal vurderes..."
                    />
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    onClick={() => setShowManualForm(false)}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={handleSaveManualQuestion}
                    disabled={savingManualQuestion || !manualQuestion.question_text.trim()}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {savingManualQuestion ? 'Lagrer...' : 'Legg til spørsmål'}
                  </button>
                </div>
              </div>
            )}

            {/* Bank Questions List */}
            {loadingBankQuestions ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 size={24} className="animate-spin mr-2" />
                Laster spørsmål...
              </div>
            ) : bankQuestions.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <Database size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-600 font-medium">
                  {bankFilter === 'all' && 'Ingen spørsmål i banken ennå'}
                  {bankFilter === 'draft' && 'Ingen utkast til spørsmål'}
                  {bankFilter === 'approved' && 'Ingen godkjente spørsmål'}
                  {bankFilter === 'rejected' && 'Ingen avviste spørsmål'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {bankFilter === 'all'
                    ? 'Klikk "Generer spørsmål fra litteratur" for å lage spørsmål basert på studieplanens litteratur'
                    : 'Endre filter eller generer nye spørsmål fra litteratur'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {bankQuestions.filter(bq => bq.status !== 'rejected').map((bq) => (
                  <div
                    key={bq.id}
                    className={`border rounded-lg p-4 ${
                      bq.status === 'approved' ? 'border-green-200 bg-green-50' :
                      bq.status === 'rejected' ? 'border-red-200 bg-red-50' :
                      'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            bq.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                            bq.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {bq.difficulty === 'easy' ? 'Enkel' : bq.difficulty === 'hard' ? 'Vanskelig' : 'Middels'}
                          </span>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {bq.question_type === 'multiple_choice' ? 'Flervalg' :
                             bq.question_type === 'true_false' ? 'Sant/Usant' : 'Skriftlig'}
                          </span>
                          {bq.status === 'approved' && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded flex items-center gap-1">
                              <CheckCircle size={12} /> Godkjent
                            </span>
                          )}
                          {bq.status === 'rejected' && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                              Avvist
                            </span>
                          )}
                          {bq.status === 'draft' && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                              Utkast
                            </span>
                          )}
                          {bq.times_used > 0 && (
                            <span className="text-xs text-gray-500">
                              Brukt {bq.times_used}x
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-900">{bq.question_text}</p>

                        {/* Show answer options for multiple choice */}
                        {bq.question_type === 'multiple_choice' && bq.answer_options && bq.answer_options.length > 0 && (
                          <div className="mt-2 pl-4 space-y-1">
                            {bq.answer_options.map((opt, idx) => (
                              <div
                                key={opt.id || idx}
                                className={`text-sm flex items-center gap-2 ${opt.is_correct ? 'text-green-700 font-medium' : 'text-gray-600'}`}
                              >
                                <span className={`w-5 h-5 flex items-center justify-center rounded-full border text-xs ${opt.is_correct ? 'bg-green-100 border-green-400' : 'bg-gray-50 border-gray-300'}`}>
                                  {String.fromCharCode(65 + idx)}
                                </span>
                                {opt.text}
                                {opt.is_correct && <CheckCircle size={14} className="text-green-600" />}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Show explanation if available */}
                        {bq.explanation && (
                          <p className="text-xs text-kf-blue-600 mt-2 italic">💡 {bq.explanation}</p>
                        )}

                        {(bq.module_title || bq.literature_title) && (
                          <p className="text-xs text-gray-500 mt-1">
                            {bq.module_title && <span>Modul: {bq.module_title}</span>}
                            {bq.module_title && bq.literature_title && ' | '}
                            {bq.literature_title && <span>Kilde: {bq.literature_title}</span>}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Edit button - always visible */}
                        <button
                          onClick={() => openEditQuestion(bq)}
                          className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition"
                        >
                          Rediger
                        </button>

                        {bq.status !== 'approved' && (
                          <>
                            <button
                              onClick={() => handleReviewQuestion(bq.id, 'approved')}
                              disabled={reviewingQuestionId === bq.id}
                              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 transition"
                            >
                              {reviewingQuestionId === bq.id ? '...' : 'Godkjenn'}
                            </button>
                            {bq.status !== 'rejected' && (
                              <button
                                onClick={() => handleReviewQuestion(bq.id, 'rejected')}
                                disabled={reviewingQuestionId === bq.id}
                                className="px-3 py-1.5 bg-red-100 text-red-700 text-sm rounded hover:bg-red-200 disabled:opacity-50 transition"
                              >
                                Avvis
                              </button>
                            )}
                          </>
                        )}

                        {/* Allow reverting approved questions back to draft */}
                        {bq.status === 'approved' && (
                          <button
                            onClick={() => handleReviewQuestion(bq.id, 'rejected')}
                            disabled={reviewingQuestionId === bq.id}
                            className="px-3 py-1.5 bg-amber-100 text-amber-700 text-sm rounded hover:bg-amber-200 disabled:opacity-50 transition"
                          >
                            Tilbakekall
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Rejected Questions Section - Collapsible */}
                {bankQuestions.filter(bq => bq.status === 'rejected').length > 0 && (
                  <div className="mt-6 border-t pt-4">
                    <button
                      onClick={() => setShowRejectedQuestions(!showRejectedQuestions)}
                      className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition w-full"
                    >
                      {showRejectedQuestions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      <span>
                        Avviste spørsmål ({bankQuestions.filter(bq => bq.status === 'rejected').length})
                      </span>
                    </button>

                    {showRejectedQuestions && (
                      <div className="mt-3 space-y-3">
                        {bankQuestions.filter(bq => bq.status === 'rejected').map((bq) => (
                          <div
                            key={bq.id}
                            className="border border-red-200 bg-red-50 rounded-lg p-4 opacity-75"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`px-2 py-0.5 text-xs rounded ${
                                    bq.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                    bq.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {bq.difficulty === 'easy' ? 'Enkel' : bq.difficulty === 'hard' ? 'Vanskelig' : 'Middels'}
                                  </span>
                                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                    {bq.question_type === 'multiple_choice' ? 'Flervalg' :
                                     bq.question_type === 'true_false' ? 'Sant/Usant' : 'Skriftlig'}
                                  </span>
                                  <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                                    Avvist
                                  </span>
                                </div>
                                <p className="text-sm text-gray-700">{bq.question_text}</p>

                                {(bq.module_title || bq.literature_title) && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    {bq.module_title && <span>Modul: {bq.module_title}</span>}
                                    {bq.module_title && bq.literature_title && ' | '}
                                    {bq.literature_title && <span>Kilde: {bq.literature_title}</span>}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openEditQuestion(bq)}
                                  className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 transition"
                                >
                                  Rediger
                                </button>
                                <button
                                  onClick={() => handleReviewQuestion(bq.id, 'draft')}
                                  disabled={reviewingQuestionId === bq.id}
                                  className="px-3 py-1.5 bg-amber-100 text-amber-700 text-sm rounded hover:bg-amber-200 disabled:opacity-50 transition"
                                >
                                  {reviewingQuestionId === bq.id ? '...' : 'Gjenopprett'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Stats */}
            {bankQuestions.length > 0 && (
              <div className="text-sm text-gray-500 text-center pt-4 border-t">
                Totalt: {bankQuestions.length} spørsmål |
                Godkjent: {bankQuestions.filter(q => q.status === 'approved').length} |
                Utkast: {bankQuestions.filter(q => q.status === 'draft').length} |
                Avvist: {bankQuestions.filter(q => q.status === 'rejected').length}
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Add Question Section */}
            <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Legg til nytt spørsmål</p>
                  <p className="text-xs text-gray-500">Velg type spørsmål du ønsker å legge til:</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => addQuestion('multiple_choice')}
                    className="px-4 py-2 bg-kf-blue-600 text-white rounded-lg hover:bg-kf-blue-700 text-sm font-medium transition flex items-center gap-1"
                  >
                    + Flervalg
                  </button>
                  <button
                    onClick={() => addQuestion('true_false')}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition flex items-center gap-1"
                  >
                    + Sant/Usant
                  </button>
                  <button
                    onClick={() => addQuestion('written')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium transition flex items-center gap-1"
                  >
                    + Skriftlig
                  </button>
                </div>
              </div>
            </div>

            {questions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p>Ingen spørsmål lagt til ennå</p>
                <p className="text-sm mt-2">Bruk knappene over for å legge til spørsmål</p>
              </div>
            ) : (
          <div className="space-y-4">
            {questions.map((question, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleQuestionExpanded(index)}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">#{index + 1}</span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {getQuestionTypeLabel(question.question_type)}
                    </span>
                    <span className="text-sm text-gray-600">
                      {Number(question.points)} poeng
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteQuestion(index);
                      }}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 size={18} />
                    </button>
                    {expandedQuestions.has(index) ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>

                {expandedQuestions.has(index) && (
                  <div className="p-4 border-t border-gray-200 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Spørsmål *
                      </label>
                      <textarea
                        value={question.question_text}
                        onChange={(e) => updateQuestion(index, { question_text: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
                        placeholder="Skriv spørsmålet her..."
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Poeng
                        </label>
                        <input
                          type="number"
                          value={question.points}
                          onChange={(e) => updateQuestion(index, { points: parseInt(e.target.value) || 1 })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
                          min="1"
                          max="10"
                        />
                      </div>
                    </div>

                    {/* Multiple Choice Options */}
                    {question.question_type === 'multiple_choice' && question.options && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Svaralternativer (velg riktig svar)
                        </label>
                        <div className="space-y-2">
                          {question.options.map((option, optIndex) => (
                            <div key={optIndex} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`correct-${index}`}
                                checked={option.is_correct}
                                onChange={() => setCorrectOption(index, optIndex)}
                                className="text-kf-blue-600 focus:ring-kf-blue-500"
                              />
                              <input
                                type="text"
                                value={option.text}
                                onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
                                placeholder={`Alternativ ${optIndex + 1}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* True/False Options */}
                    {question.question_type === 'true_false' && question.options && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Riktig svar
                        </label>
                        <div className="space-y-2">
                          {question.options.map((option, optIndex) => (
                            <label key={optIndex} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`correct-${index}`}
                                checked={option.is_correct}
                                onChange={() => setCorrectOption(index, optIndex)}
                                className="text-kf-blue-600 focus:ring-kf-blue-500"
                              />
                              <span className="text-gray-700">{option.text}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Written Question Grading Criteria */}
                    {question.question_type === 'written' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Vurderingskriterier (for KI-sensurering)
                        </label>
                        <textarea
                          value={question.grading_criteria || ''}
                          onChange={(e) => updateQuestion(index, { grading_criteria: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
                          placeholder="Beskriv hva som skal vurderes i svaret..."
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
            )}
          </>
        )}
      </div>

      {/* Edit Question Modal */}
      {editingQuestion && editFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Rediger spørsmål</h3>
                <button
                  onClick={() => { setEditingQuestion(null); setEditFormData(null); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spørsmålstekst *
                </label>
                <textarea
                  value={editFormData.question_text}
                  onChange={(e) => setEditFormData({ ...editFormData, question_text: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vanskelighetsgrad
                </label>
                <select
                  value={editFormData.difficulty}
                  onChange={(e) => setEditFormData({ ...editFormData, difficulty: e.target.value as 'easy' | 'medium' | 'hard' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="easy">Enkel</option>
                  <option value="medium">Middels</option>
                  <option value="hard">Vanskelig</option>
                </select>
              </div>

              {/* Answer Options (for multiple choice) */}
              {editingQuestion.question_type === 'multiple_choice' && editFormData.answer_options.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Svaralternativer (velg riktig svar)
                  </label>
                  <div className="space-y-2">
                    {editFormData.answer_options.map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="edit-correct-option"
                          checked={opt.is_correct}
                          onChange={() => setEditFormData({
                            ...editFormData,
                            answer_options: editFormData.answer_options.map((o, i) => ({
                              ...o,
                              is_correct: i === idx
                            }))
                          })}
                          className="text-purple-600 focus:ring-purple-500"
                        />
                        <input
                          type="text"
                          value={opt.text}
                          onChange={(e) => setEditFormData({
                            ...editFormData,
                            answer_options: editFormData.answer_options.map((o, i) =>
                              i === idx ? { ...o, text: e.target.value } : o
                            )
                          })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                          placeholder={`Alternativ ${idx + 1}`}
                        />
                        {opt.is_correct && (
                          <CheckCircle size={18} className="text-green-600" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* True/False Options */}
              {editingQuestion.question_type === 'true_false' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Riktig svar
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="edit-tf-correct"
                        checked={editFormData.answer_options[0]?.is_correct}
                        onChange={() => setEditFormData({
                          ...editFormData,
                          answer_options: [
                            { text: 'Sant', is_correct: true },
                            { text: 'Usant', is_correct: false }
                          ]
                        })}
                        className="text-purple-600"
                      />
                      Sant
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="edit-tf-correct"
                        checked={editFormData.answer_options[1]?.is_correct}
                        onChange={() => setEditFormData({
                          ...editFormData,
                          answer_options: [
                            { text: 'Sant', is_correct: false },
                            { text: 'Usant', is_correct: true }
                          ]
                        })}
                        className="text-purple-600"
                      />
                      Usant
                    </label>
                  </div>
                </div>
              )}

              {/* Explanation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Forklaring (vises etter svar)
                </label>
                <textarea
                  value={editFormData.explanation}
                  onChange={(e) => setEditFormData({ ...editFormData, explanation: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Valgfri forklaring på riktig svar..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setEditingQuestion(null); setEditFormData(null); }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Avbryt
              </button>
              <button
                onClick={handleSaveEditQuestion}
                disabled={savingEditQuestion || !editFormData.question_text.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-medium"
              >
                {savingEditQuestion ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamCreator;
// Build timestamp: 1773491137
